import { startOfLocalDay } from './readinessUtils';

export const ACWR_WARMUP_DAYS = 28;

const DAY_MS = 24 * 60 * 60 * 1000;

// Whole local calendar days, so DST (23/25-hour days) and time of day never shift a boundary.
const calendarDay = date => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS;

// A true zero ACWR reaches the app as 0 (legacy backfill, frontend rest days) or as null with
// ATL and CTL both 0 (the API stores null whenever ATL or CTL is falsy). A null ACWR with
// missing ATL/CTL is unknown, not zero.
const isZeroAcwr = workout =>
    workout.acwr === 0 || (workout.acwr === null && workout.atl === 0 && workout.ctl === 0);

// Warm-up windows from the full workout history: [{ start, end }], where start is the local day
// of a Completed workout and end is the day ACWR reached zero (null while still running). A
// window opens on the first Completed workout, again on the next chronologically later
// Completed workout after a zero (even later the same day), and again on any Completed workout
// more than 28 calendar days after the previous Completed one (the open window ends there).
// Assigned and Declined workouts are ignored.
export const getAcwrWarmupWindows = workouts => {
    const completed = workouts
        .filter(workout => workout.status === 'Completed')
        .map(workout => ({ workout, time: new Date(workout.workoutStart).getTime() }))
        .filter(({ time }) => !Number.isNaN(time))
        .sort((a, b) => a.time - b.time);

    // Every Completed record is processed in time order, so a record later on the same day as a
    // zero still opens the restart. The zero is tracked apart from the open window: the zero
    // record never starts a window itself, the next later Completed record does.
    const windows = [];
    let current = null;
    let zeroSeen = false;
    let previousDay = null;
    completed.forEach(({ workout }) => {
        const start = startOfLocalDay(workout.workoutStart);
        const day = calendarDay(start);
        const longGap = previousDay !== null && day - previousDay > ACWR_WARMUP_DAYS;
        previousDay = day;
        if (!current || zeroSeen || longGap) {
            if (current && !current.end) {
                current.end = start;
            }
            current = { start, end: null };
            windows.push(current);
            zeroSeen = false;
        }
        if (isZeroAcwr(workout)) {
            current.end = start;
            zeroSeen = true;
        }
    });
    return windows;
};

// ACWR is shown from day 28 of a window (days 0-27 are warm-up) until the day it reaches zero.
// Days outside every window (before the first workout, or after a zero until the next
// Completed workout) have no reliable ACWR either.
export const isAcwrHidden = (windows, date) => {
    const day = calendarDay(startOfLocalDay(date));
    return !windows.some(
        ({ start, end }) =>
            day - calendarDay(start) >= ACWR_WARMUP_DAYS && (!end || day < calendarDay(end))
    );
};
